import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { BuildingOfficeIcon, PhoneIcon, MagnifyingGlassIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { showToast } from '../common/Toast';
import { businessPermitsAPI } from '../../services/api/admin/businessPermitApi';
import { useMutation, useQuery } from 'react-query';
import { businessRegistrationSchema } from '../../validation/businessPermit/businessRegistrationSchema';
import { RESIDENT_STATUS, CLASSIFICATIONS, MIN_SEARCH_LENGTH } from '../../constant/businessConstants';
import { formatOwnerName, buildBusinessAddress } from '../../utils/businessUtils';
import FormInput from '../common/FormInput';
import FormSelect from '../common/FormSelect';
import RadioButton from '../common/RadioButton';
import SectionHeader from '../common/SectionHeader';
import { fetchRegions, fetchProvincesForRegions, fetchMunicipalitiesForProvinces, fetchBarangaysForMunicipalities } from '../../services/psgcApi';
import NewBusinessTypeModal from './Newbusinesstypemodal';
import NewNatureOfBusiness from './Newnatureofbusiness';
import { useFetch } from '../../hooks/useCallApi';

const BUSINESS_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

/**
 * Business Registration Modal Component
 */
const BusinessRegistrationModal = ({
  isOpen,
  onClose,
  initialData = null,
  onSubmit,
  isLoading = false,
}) => {

  const [selectedResidentStatus, setSelectedResidentStatus] = useState(RESIDENT_STATUS.RESIDENT);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isBusinessTypeModalOpen, setIsBusinessTypeModalOpen] = useState(false);
  const [isNatureOfBusinessModalOpen, setIsNatureOfBusinessModalOpen] = useState(false);

  // Address dropdown states for non-residents
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [barangays, setBarangays] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    trigger,
    watch,
    control,
  } = useForm({
    resolver: yupResolver(businessRegistrationSchema),
    defaultValues: {
      ...initialData,
      business_type_id: initialData?.business_type?.id ?? initialData?.business_type_id ?? initialData?.business_type ?? '',
      business_nature_id: initialData?.business_nature?.id ?? initialData?.business_nature_id ?? initialData?.business_nature ?? '',
      is_resident: initialData?.is_resident?.toString() || RESIDENT_STATUS.RESIDENT,
      status: initialData?.status ?? 'pending',
    },
  });

  const normalizeInitialDataForForm = useCallback((data) => {
    if (!data) return null;

    const isResidentBool = data?.is_resident === true || data?.is_resident === 'true' || data?.is_resident === 1;
    const residentStatus = isResidentBool ? RESIDENT_STATUS.RESIDENT : RESIDENT_STATUS.NON_RESIDENT;

    const address = data?.address || {};

    return {
      ...data,
      business_type_id:
        data?.business_type?.id ??
        data?.business_type?.type_id ??
        data?.business_type_id ??
        data?.business_type ??
        '',
      business_nature_id:
        data?.business_nature?.id ??
        data?.business_nature?.nature_id ??
        data?.business_nature_id ??
        data?.business_nature ??
        '',
      is_resident: residentStatus,
      owner_id: data?.owner_id ?? data?.owner?.id ?? data?.owner?.resident_id ?? null,
      name: data?.name ?? data?.owner_name ?? data?.owner?.name ?? '',
      street_name: data?.street_name ?? address?.street ?? '',
      purok_zone: data?.purok_zone ?? address?.purok_zone ?? '',
      barangay: data?.barangay ?? address?.barangay ?? '',
      municipality: data?.municipality ?? address?.municipality ?? '',
      province: data?.province ?? address?.province ?? '',
      region: data?.region ?? address?.region ?? '',
      zip_code: data?.zip_code ?? address?.zip_code ?? '',
      status: data?.status ?? 'pending',
    };
  }, []);

  const normalizeBusinessTypesResponse = (response) => {
    return (
      response?.data?.business_types ||
      (Array.isArray(response?.data) ? response.data : null) ||
      response?.business_types ||
      (Array.isArray(response) ? response : []) ||
      []
    );
  };

  const normalizeNatureOfBusinessResponse = (response) => {
    return (
      response?.data?.businesse_natures ||
      response?.data?.business_natures ||
      (Array.isArray(response?.data) ? response.data : null) ||
      response?.business_natures ||
      (Array.isArray(response) ? response : []) ||
      []
    );
  };

  const businessTypeParams = { per_page: 1000, page: 1 };
  const natureOfBusinessParams = { per_page: 1000, page: 1 };

  const { data: businessTypesData } = useFetch(
    ['business-types'],
    (api) => api.admin.businessPermitsAPI.getAllBusinessTypes(businessTypeParams),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      enabled: isOpen,
      onError: () => {
        showToast('Failed to load business types', 'error');
      },
    }
  );

  const { data: natureOfBusinessData } = useFetch(
    ['nature-of-business'],
    (api) => api.admin.businessPermitsAPI.getAllNatureOfBusiness(natureOfBusinessParams),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      enabled: isOpen,
      onError: () => {
        showToast('Failed to load nature of business', 'error');
      },
    }
  );

  const businessTypeOptions = normalizeBusinessTypesResponse(businessTypesData).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const natureOfBusinessOptions = normalizeNatureOfBusinessResponse(natureOfBusinessData).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  console.log("businessTypeOptions", businessTypeOptions);
  console.log("natureOfBusinessOptions", natureOfBusinessOptions);

  // Query for searching owners
  const { data: searchResults = [], isLoading: isSearchingOwner } = useQuery(
    ['search-owners', searchQuery],
    () => businessPermitsAPI.getOwners({ search: searchQuery }),
    {
      enabled: searchQuery.length >= MIN_SEARCH_LENGTH && selectedResidentStatus === RESIDENT_STATUS.RESIDENT,
      select: (response) => response.data?.data?.searched_resident || [],
      onError: (error) => {
        console.error('Error searching owners:', error);
        showToast('Failed to search owners', 'error');
      },
    }
  );

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (initialData) {
      const normalized = normalizeInitialDataForForm(initialData);
      const residentStatus = normalized?.is_resident || RESIDENT_STATUS.RESIDENT;

      reset(normalized);
      setSelectedResidentStatus(residentStatus);

      if (residentStatus === RESIDENT_STATUS.RESIDENT && normalized?.owner_id) {
        setSelectedOwner({
          id: normalized.owner_id,
          full_name: normalized?.owner?.name || normalized?.name || '',
        });
      } else {
        setSelectedOwner(null);
      }
    } else {
      reset({ is_resident: RESIDENT_STATUS.RESIDENT, business_type_id: '', business_nature_id: '' });
      setSelectedResidentStatus(RESIDENT_STATUS.RESIDENT);
      setSelectedOwner(null);
    }
    setSearchQuery('');
  }, [initialData, reset, normalizeInitialDataForForm]);

  const clearOwnerData = useCallback(() => {
    setSelectedOwner(null);
    setSearchQuery('');
    setValue('name', '');
    setValue('owner_id', null);
  }, [setValue]);

  const clearAddressFields = useCallback(() => {
    ['street_name', 'purok_zone', 'barangay', 'municipality', 'province', 'region', 'zip_code'].forEach(
      field => setValue(field, '')
    );
  }, [setValue]);

  const handleResidentStatusChange = useCallback((value) => {
    setSelectedResidentStatus(value);
    setValue('is_resident', value);

    clearOwnerData();
    clearAddressFields();
    setProvinces([]);
    setMunicipalities([]);
    setBarangays([]);

    // When switching back to resident, clear owner_id to force re-selection
    if (value === RESIDENT_STATUS.RESIDENT) {
      setValue('owner_id', null);
    }
  }, [setValue, clearOwnerData, clearAddressFields]);

  const handleOwnerSearch = useCallback((e) => {
    const value = e.target.value;
    setValue('name', value);
    setSearchQuery(value);
  }, [setValue]);

  const populateAddressFields = useCallback((address) => {
    if (!address) return;

    const addressFields = {
      street_name: address.street || '',
      purok_zone: address.purok_zone || '',
      barangay: address.barangay || '',
      municipality: address.municipality || '',
      province: address.province || '',
      region: address.region || '',
      zip_code: address.zip_code || '',
    };

    Object.entries(addressFields).forEach(([field, value]) => {
      setValue(field, value);
    });
  }, [setValue]);

  const handleSelectOwner = useCallback((owner) => {
    setSelectedOwner(owner);
    setValue('name', formatOwnerName(owner));
    setValue('owner_id', owner.id);
    setSearchQuery('');
    populateAddressFields(owner);
  }, [setValue, populateAddressFields]);

  const handleFormSubmit = async (data) => {
    console.log('data before submit', data);
    console.log('businessTypeOptions', businessTypeOptions);
    console.log('natureOfBusinessOptions', natureOfBusinessOptions);
    const isValid = await trigger();

    if (!isValid) {
      const firstError = Object.values(errors)[0];
      if (firstError) {
        showToast(firstError.message, 'error');
      }
      return;
    }

    const isResidentSubmit = data.is_resident === RESIDENT_STATUS.RESIDENT;

    const submitData = {
      ...data,
      name: isResidentSubmit ? "" : (data.name || ""),
      business_address: buildBusinessAddress(data),
      business_nature_id: data.business_nature_id,
      is_resident: isResidentSubmit,
      owner_id: data.owner_id,
    };
    onSubmit?.(submitData);
  };

  const handleClose = useCallback(() => {
    reset({
      business_name: '',
      business_type_id: '',
      business_nature_id: '',
      classification: '',
      is_resident: RESIDENT_STATUS.RESIDENT,
      owner_id: null,
      name: '',
      region: '',
      province: '',
      municipality: '',
      barangay: '',
      street_name: '',
      purok_zone: '',
      zip_code: '',
    });
    setSelectedResidentStatus(RESIDENT_STATUS.RESIDENT);
    setSelectedOwner(null);
    setSearchQuery('');
    setProvinces([]);
    setMunicipalities([]);
    setBarangays([]);
    onClose();
  }, [reset, onClose]);

  const isResident = selectedResidentStatus === RESIDENT_STATUS.RESIDENT;
  const showSearchResults = !isSearchingOwner && searchResults.length > 0;

  // Address dropdown effects for non-residents
  const selectedRegion = watch('region');
  const selectedProvince = watch('province');
  const selectedMunicipality = watch('municipality');

  // Fetch regions on component mount
  useEffect(() => {
    const getRegions = async () => {
      try {
        const regionsData = await fetchRegions();
        setRegions(regionsData);
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };
    getRegions();
  }, []);

  // Fetch provinces when region changes (only for non-residents)
  useEffect(() => {
    if (!selectedRegion || isResident) return;

    const getProvinces = async () => {
      try {
        const provincesData = await fetchProvincesForRegions([selectedRegion]);
        setProvinces(provincesData);
        setMunicipalities([]);
        setBarangays([]);
        setValue('province', '');
        setValue('municipality', '');
        setValue('barangay', '');
      } catch (error) {
        console.error('Error fetching provinces:', error);
        setProvinces([]);
      }
    };
    getProvinces();
  }, [selectedRegion, isResident, setValue]);

  // Fetch municipalities when province changes (only for non-residents)
  useEffect(() => {
    if (!selectedProvince || isResident) return;

    const getMunicipalities = async () => {
      try {
        const municipalitiesData = await fetchMunicipalitiesForProvinces([selectedProvince]);
        setMunicipalities(municipalitiesData);
        setBarangays([]);
        setValue('municipality', '');
        setValue('barangay', '');
      } catch (error) {
        console.error('Error fetching municipalities:', error);
        setMunicipalities([]);
      }
    };
    getMunicipalities();
  }, [selectedProvince, isResident, setValue]);

  // Fetch barangays when municipality changes (only for non-residents)
  useEffect(() => {
    if (!selectedMunicipality || isResident) return;

    const getBarangays = async () => {
      try {
        const barangaysData = await fetchBarangaysForMunicipalities([selectedMunicipality]);
        setBarangays(barangaysData);
        setValue('barangay', '');
      } catch (error) {
        console.error('Error fetching barangays:', error);
        setBarangays([]);
      }
    };
    getBarangays();
  }, [selectedMunicipality, isResident, setValue]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={initialData ? 'Update Business' : 'Register New Business'}
      size="6xl"
    >
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-8 max-h-[80vh] overflow-y-auto px-2"
      >
        {/* Business Information Section */}
        <div className="space-y-4">
          <SectionHeader icon={BuildingOfficeIcon} title="Business Information" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Resident Status */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resident Status <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <RadioButton
                  selected={selectedResidentStatus === RESIDENT_STATUS.RESIDENT}
                  onClick={() => handleResidentStatusChange(RESIDENT_STATUS.RESIDENT)}
                  label="Resident"
                />
                <RadioButton
                  selected={selectedResidentStatus === RESIDENT_STATUS.NON_RESIDENT}
                  onClick={() => handleResidentStatusChange(RESIDENT_STATUS.NON_RESIDENT)}
                  label="Non-Resident"
                />
              </div>
              <input type="hidden" {...register('is_resident')} />
              <input type="hidden" {...register('owner_id')} />
              {errors.is_resident && (
                <p className="text-red-500 text-xs mt-1">{errors.is_resident.message}</p>
              )}
            </div>

            <FormInput
              label="Business Name"
              required
              register={register('business_name')}
              error={errors.business_name}
              placeholder="Enter business name"
            />

            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                <button
                  type="button"
                  className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition"
                  onClick={() => setIsBusinessTypeModalOpen(true)}
                >
                  + Add New Business Type
                </button>
              </div>
              <Controller
                name="business_type_id"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    label=""
                    error={errors.business_type_id}
                    options={businessTypeOptions}
                    placeholder="Select business type"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nature of Business</label>
                <button
                  type="button"
                  className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition"
                  onClick={() => setIsNatureOfBusinessModalOpen(true)}
                >
                  + Add New Nature of Business
                </button>
              </div>
              <Controller
                name="business_nature_id"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    label=""
                    error={errors.business_nature_id}
                    options={natureOfBusinessOptions}
                    placeholder="Select nature of business"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>

            {/* Owner Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner <span className="text-red-500">*</span>
              </label>
              {isResident ? (
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    {...register('name')}
                    onChange={handleOwnerSearch}
                    placeholder="Search for resident by name"
                    autoComplete="off"
                    className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 pl-10 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300"
                  />
                  {/* Search Results Dropdown */}
                  {showSearchResults && (
                    <ul className="absolute z-[60] bg-white border rounded-md mt-1 w-full max-h-40 overflow-y-auto shadow">
                      {searchResults.map((owner) => (
                        <li
                          key={owner.id}
                          onClick={() => handleSelectOwner(owner)}
                          className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                        >
                          {formatOwnerName(owner)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {isSearchingOwner && (
                    <div className="absolute mt-1 text-gray-400 text-sm px-2 z-[60]">
                      Searching...
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  {...register('name')}
                  placeholder="Enter owner name"
                  className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300"
                />
              )}
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
              )}
              {errors.owner_id && (
                <p className="text-red-500 text-xs mt-1">{errors.owner_id.message}</p>
              )}
            </div>

            <Controller
              name="classification"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label="Classification"
                  required
                  error={errors.classification}
                  options={CLASSIFICATIONS}
                  placeholder="Select classification"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />

            {/* {initialData && (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    label="Status"
                    error={errors.status}
                    options={BUSINESS_STATUS_OPTIONS}
                    placeholder="Select status"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            )} */}
          </div>
        </div>

        {/* Business Address Section */}
        <div className="space-y-4">
          <SectionHeader icon={PhoneIcon} title="Business Address" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Region field */}
            {!isResident ? (
              <div className="group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Region <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('region', { required: 'Region is required' })}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Region</option>
                  {regions.map((region) => (
                    <option key={region.code} value={region.code}>
                      {region.name}
                    </option>
                  ))}
                </select>
                {errors.region && (
                  <p className="text-red-500 text-xs mt-1">{errors.region.message}</p>
                )}
              </div>
            ) : (
              <FormInput
                label="Region"
                required
                register={register('region')}
                error={errors.region}
                placeholder="Enter region"
              />
            )}

            {/* Province field */}
            {!isResident ? (
              <div className="group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Province <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('province', { required: 'Province is required' })}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  disabled={!selectedRegion}
                >
                  <option value="">
                    {selectedRegion ? 'Select Province' : 'Select Region First'}
                  </option>
                  {provinces.map((province) => (
                    <option key={province.code} value={province.code}>
                      {province.name}
                    </option>
                  ))}
                </select>
                {errors.province && (
                  <p className="text-red-500 text-xs mt-1">{errors.province.message}</p>
                )}
              </div>
            ) : (
              <FormInput
                label="Province"
                required
                register={register('province')}
                error={errors.province}
                placeholder="Enter province"
              />
            )}

            {/* Municipality field */}
            {!isResident ? (
              <div className="group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Municipality <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('municipality', { required: 'Municipality is required' })}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  disabled={!selectedProvince}
                >
                  <option value="">
                    {selectedProvince
                      ? 'Select Municipality'
                      : 'Select Province First'}
                  </option>
                  {municipalities.map((municipality) => (
                    <option key={municipality.code} value={municipality.code}>
                      {municipality.name}
                    </option>
                  ))}
                </select>
                {errors.municipality && (
                  <p className="text-red-500 text-xs mt-1">{errors.municipality.message}</p>
                )}
              </div>
            ) : (
              <FormInput
                label="Municipality"
                required
                register={register('municipality')}
                error={errors.municipality}
                placeholder="Enter municipality"
              />
            )}

            {/* Barangay field */}
            {!isResident ? (
              <div className="group">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Barangay <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('barangay', { required: 'Barangay is required' })}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  disabled={!selectedMunicipality}
                >
                  <option value="">
                    {selectedMunicipality
                      ? 'Select Barangay'
                      : 'Select Municipality First'}
                  </option>
                  {barangays.map((barangay) => (
                    <option key={barangay.code} value={barangay.code}>
                      {barangay.name}
                    </option>
                  ))}
                </select>
                {errors.barangay && (
                  <p className="text-red-500 text-xs mt-1">{errors.barangay.message}</p>
                )}
              </div>
            ) : (
              <FormInput
                label="Barangay"
                required
                register={register('barangay')}
                error={errors.barangay}
                placeholder="Enter barangay"
              />
            )}

            <FormInput
              label="Street Name"
              register={register('street_name')}
              error={errors.street_name}
              placeholder="Enter street name"
              required
            />

            <FormInput
              label="Purok"
              register={register('purok_zone')}
              error={errors.purok_zone}
              placeholder="Enter purok"
              required
            />

            <FormInput
              label="ZIP Code"
              register={register('zip_code')}
              error={errors.zip_code}
              placeholder="Enter ZIP code"
              required
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
          >
            {isLoading
              ? 'Saving...'
              : (initialData ? 'Update Business' : 'Register Business')}
          </Button>
        </div>
      </form>

      {/* CMS Modals */}
      <NewBusinessTypeModal
        isOpen={isBusinessTypeModalOpen}
        onClose={() => setIsBusinessTypeModalOpen(false)}
      />

      <NewNatureOfBusiness
        isOpen={isNatureOfBusinessModalOpen}
        onClose={() => setIsNatureOfBusinessModalOpen(false)}
      />
    </Modal>
  );
};

export default BusinessRegistrationModal;